import { useState, useEffect } from 'react';
import { Gift, Heart, Sparkles, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * WelcomeBanner
 *
 * First-run onboarding shown on the Keepsakes page for brand-new users who
 * have zero recipients AND zero wishlists. Dismissible, once closed, never
 * comes back (tracked via `localStorage`). Renders nothing if either the
 * user has activity or has explicitly dismissed it.
 *
 * The banner is intentionally lightweight: a 3-step visual primer that
 * explains the product loop (Create → Save → AI suggestions) without
 * blocking anything. Each step doubles as a quick-jump CTA.
 */
const STORAGE_KEY = 'mementa_onboarding_dismissed';

export default function WelcomeBanner({ user, hasActivity, darkMode }) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Trigger entrance after mount so the banner gracefully fades in.
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  if (dismissed || hasActivity) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // localStorage unavailable, just hide for the session.
    }
    setDismissed(true);
  };

  const firstName = user?.name?.split(' ')[0] || 'there';

  const steps = [
    {
      icon: Gift,
      title: 'Create a Keepsake',
      body: 'Add someone you love and capture their tastes, interests, and the occasions worth remembering.',
      cta: 'Add your first person',
      onClick: () => {
        // Scroll to the existing "Add Recipient" button, keeps the user in
        // the same flow without yanking them to a new page.
        const btn = document.querySelector('[data-testid="add-first-recipient-button"]')
          || document.querySelector('[data-testid="add-recipient-button"]');
        btn?.click();
      }
    },
    {
      icon: Heart,
      title: 'Save gifts you love',
      body: 'Browse the catalog and stash favorites onto each keepsake. Nothing is lost between birthdays.',
      cta: 'Open the catalog',
      onClick: () => navigate('/browse')
    },
    {
      icon: Sparkles,
      title: 'Get AI suggestions',
      body: 'Send a quick questionnaire or let our AI surface picks tuned to who they are, not who Google thinks they are.',
      cta: 'See it in action',
      onClick: () => navigate('/browse')
    }
  ];

  return (
    <div
      className="relative mb-10 rounded-2xl overflow-hidden transition-all duration-500"
      style={{
        background:
          'linear-gradient(135deg, #2D4354 0%, #534145 55%, #9E6752 100%)',
        boxShadow: '0 12px 36px rgba(45, 67, 84, 0.35)',
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(12px)'
      }}
      data-testid="welcome-banner"
    >
      {/* Painted halo behind the icons */}
      <div
        aria-hidden
        className="absolute -top-20 -right-20 w-72 h-72 rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(254,215,165,0.3), transparent 70%)' }}
      />

      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss welcome banner"
        data-testid="welcome-banner-dismiss"
        className="absolute top-3 right-3 z-10 p-2 rounded-full transition-colors hover:bg-white/10"
        style={{ color: 'rgba(255,255,255,0.75)' }}
      >
        <X className="w-4 h-4" />
      </button>

      <div className="relative px-6 py-7 sm:px-10 sm:py-9">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4" style={{ color: '#FED7A5' }} />
          <span
            className="text-xs uppercase tracking-[0.18em]"
            style={{ color: '#FED7A5', fontWeight: 600 }}
          >
            Welcome to Mementa
          </span>
        </div>
        <h2
          className="text-2xl sm:text-3xl mb-2"
          style={{
            fontFamily: 'Playfair Display, serif',
            color: '#FFFAF5',
            fontWeight: 600
          }}
        >
          Hi {firstName}, let&rsquo;s set the stage.
        </h2>
        <p
          className="text-sm sm:text-base mb-7 max-w-2xl"
          style={{ color: 'rgba(255, 250, 245, 0.78)' }}
        >
          Three quick steps and you&rsquo;ll have a thoughtful gifting setup
          working in the background for everyone you care about.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <div
                key={step.title}
                className="rounded-xl p-4 backdrop-blur-sm transition-all hover:scale-[1.015] hover:transform-none"
                style={{
                  background: 'rgba(255, 250, 245, 0.06)',
                  border: '1px solid rgba(254, 215, 165, 0.25)'
                }}
                data-testid={`welcome-step-${idx + 1}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="text-xs font-semibold"
                    style={{ color: '#FED7A5' }}
                  >
                    Step {idx + 1}
                  </span>
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center"
                    style={{
                      background: 'rgba(254, 215, 165, 0.18)',
                      border: '1px solid rgba(254, 215, 165, 0.5)'
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: '#FED7A5' }} />
                  </div>
                </div>
                <h3
                  className="text-base mb-1.5"
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    color: '#FFFAF5',
                    fontWeight: 600
                  }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-xs leading-relaxed mb-3"
                  style={{ color: 'rgba(255, 250, 245, 0.7)' }}
                >
                  {step.body}
                </p>
                <button
                  type="button"
                  onClick={step.onClick}
                  className="text-xs font-semibold underline-offset-4 hover:underline transition-colors hover:transform-none"
                  style={{ color: '#FED7A5' }}
                  data-testid={`welcome-step-${idx + 1}-cta`}
                >
                  {step.cta} &rarr;
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
